import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, Trash2, Home, FileText, Info,
  CreditCard, LineChart, UserCircle2, Clock,
  Hash, ShoppingBag, ChevronDown, ChevronUp, MapPin,
  Users, Map, Calendar, Plus, X, Loader2, Lock,
  Send, Banknote, CheckCircle, RotateCcw, ChevronRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatMoney, getMerchantAvatar } from "../utils/mindeeParser";
import { formatReceiptDate, getGroupTypeConfig, getMemberAvatarColor } from "../utils/formatters";

const CURRENCY_SYMBOLS = { GBP: '£', INR: '₹', USD: '$', EUR: '€', AED: 'د.إ', AUD: 'A$' };
const formatCurrency = (amount, currency) => {
  const symbol = CURRENCY_SYMBOLS[(currency || '').toUpperCase()] || `${currency || ''} `;
  return `${symbol}${(amount || 0).toFixed(2)}`;
};
import { DeleteDialog, Lightbox, ImagePlaceholder, Shimmer } from "../components/ReceiptDetailModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GRADIENT = "linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)";
const PRIMARY = "#2D5BFF";

const NAV_ITEMS = [
  { icon: Home, label: "Home", key: "home" },
  { icon: FileText, label: "Receipts", key: "receipts" },
  { icon: CreditCard, label: "Cards", key: "cards" },
  { icon: LineChart, label: "Insights", key: "insights" },
  { icon: UserCircle2, label: "Profile", key: "profile" },
];

// ── Merchant Avatar ─────────────────────────────────────────────────────────
const MerchantAvatar = ({ name, size = 44 }) => {
  const { initials, bgColor, textColor } = getMerchantAvatar(name || "");
  return (
    <div
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: size, height: size, borderRadius: size * 0.25,
        backgroundColor: bgColor, color: textColor,
        fontWeight: 700, fontSize: size * 0.32,
        fontFamily: "Manrope, sans-serif",
        border: "1.5px solid rgba(0,0,0,0.08)",
      }}
    >
      {initials}
    </div>
  );
};

// ── Category Pill ───────────────────────────────────────────────────────────
const CategoryPill = ({ label }) => {
  const colors = {
    Groceries:      { bg: "#DCFCE7", color: "#15803D" },
    Dining:         { bg: "#FEF3C7", color: "#B45309" },
    Transport:      { bg: "#EEF2FF", color: "#4338CA" },
    Health:         { bg: "#F0FDF4", color: "#16A34A" },
    Shopping:       { bg: "#FDF2F8", color: "#9D174D" },
    Entertainment:  { bg: "#FFF7ED", color: "#C2410C" },
    "Home & Garden":{ bg: "#ECFDF5", color: "#065F46" },
    Other:          { bg: "#F1F5F9", color: "#475569" },
  };
  const c = colors[label] || colors.Other;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: c.bg, color: c.color }}>
      {label || "Other"}
    </span>
  );
};

// ── Info Row with icon ──────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value, isLink = false }) => {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between py-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span style={{ fontSize: 13, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>{label}</span>
      </div>
      <span
        style={{
          fontSize: 13, fontWeight: 600,
          color: isLink ? PRIMARY : "#111827",
          fontFamily: "DM Sans, sans-serif",
          textAlign: "right", maxWidth: "60%",
        }}
      >
        {value}
      </span>
    </div>
  );
};

// ── Item Row ────────────────────────────────────────────────────────────────
const ItemRow = ({ item, currency }) => {
  const hasDiscount = item.discount_amount > 0;
  const displayPrice = item.final_total ?? item.unit_price ?? null;
  return (
    <div className="flex items-start justify-between py-3" style={{ borderBottom: "1px solid #F8FAFC" }}>
      <div className="flex-1 min-w-0 pr-4">
        <p style={{ fontSize: 14, fontWeight: 500, color: "#111827", fontFamily: "Manrope, sans-serif", lineHeight: 1.4 }}>
          {item.name}
        </p>
        <div className="flex flex-col gap-1 mt-1">
          {item.quantity > 1 && (
            <span style={{ fontSize: 12, color: "#9CA3AF", fontFamily: "DM Sans, sans-serif" }}>
              ×{item.quantity} @ {formatMoney(item.unit_price, currency)} each
            </span>
          )}
          {hasDiscount && item.discount_reason && (
            <span style={{ fontSize: 11, fontWeight: 600, color: "#15803D", backgroundColor: "#DCFCE7", padding: "2px 7px", borderRadius: 20, fontFamily: "DM Sans, sans-serif", alignSelf: "flex-start" }}>
              {item.discount_reason} · save {formatMoney(item.discount_amount, currency)}
            </span>
          )}
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        {hasDiscount && item.original_total > 0 && (
          <p style={{ fontSize: 12, color: "#9CA3AF", textDecoration: "line-through", fontFamily: "DM Sans, sans-serif" }}>
            {formatMoney(item.original_total, currency)}
          </p>
        )}
        {displayPrice !== null && (
          <p style={{ fontSize: 14, fontWeight: 600, color: "#111827", fontFamily: "Manrope, sans-serif", whiteSpace: "nowrap" }}>
            {formatMoney(displayPrice, currency)}
          </p>
        )}
      </div>
    </div>
  );
};

// ── Skeleton ────────────────────────────────────────────────────────────────
const SkeletonPage = () => (
  <div className="space-y-4 p-4">
    <Shimmer width="100%" height={200} radius={16} />
    <Shimmer width="100%" height={100} radius={16} />
    <Shimmer width="100%" height={200} radius={16} />
  </div>
);

// ── Section Card ────────────────────────────────────────────────────────────
const SectionCard = ({ children, className = "" }) => (
  <div
    className={`bg-white rounded-2xl ${className}`}
    style={{ border: "1px solid #F3F4F6", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}
  >
    {children}
  </div>
);

// ── Main ────────────────────────────────────────────────────────────────────
export default function ReceiptDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isOwner, setIsOwner] = useState(true);
  const [sharedByName, setSharedByName] = useState(null);

  // Group sharing state
  const [sharingInfo, setSharingInfo] = useState([]); // groups this receipt is shared with
  const [allGroups, setAllGroups] = useState([]); // user's groups for the picker
  const [showSharePicker, setShowSharePicker] = useState(false);
  const [sharing, setSharing] = useState(false);

  // Reimbursement — "Who paid?" step in share flow
  const [pendingShareGroup, setPendingShareGroup] = useState(null); // group selected, awaiting paid-by
  const [circleMembers, setCircleMembers] = useState([]);
  const [circleLabels, setCircleLabels] = useState([]);
  const [paidByType, setPaidByType] = useState("member"); // "member" | "label"
  const [paidByUserId, setPaidByUserId] = useState(null);
  const [paidByName, setPaidByName] = useState(null);
  const [paidByLabelId, setPaidByLabelId] = useState(null);
  const [paidByLabelName, setPaidByLabelName] = useState(null);
  const [gettingReimbursed, setGettingReimbursed] = useState(false);
  const [reimbLabelId, setReimbLabelId] = useState(null);
  const [reimbLabelName, setReimbLabelName] = useState(null);
  const [showPaidByPicker, setShowPaidByPicker] = useState(false);
  const [showReimbPicker, setShowReimbPicker] = useState(false);
  const [paidBySearch, setPaidBySearch] = useState('');
  const [reimbSearch, setReimbSearch] = useState('');
  const [showAddLabel, setShowAddLabel] = useState(false);
  const [newLabelName, setNewLabelName] = useState("");
  const [newLabelType, setNewLabelType] = useState("bank");
  const [savingLabel, setSavingLabel] = useState(false);

  // Reimbursement — mark-as-received on receipt detail
  const [shareReimbInfo, setShareReimbInfo] = useState(null); // {share_id, paid_by_*, reimb_status, group_id, group_name, is_uploader}
  const [showMarkReceived, setShowMarkReceived] = useState(false);
  const [markReceivedNote, setMarkReceivedNote] = useState("");
  const [markingReceived, setMarkingReceived] = useState(false);
  const [undoingReceived, setUndoingReceived] = useState(false);

  const reqRef = useRef(0);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const fetchReceipt = useCallback(async () => {
    const reqId = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/receipts/${id}`, { withCredentials: true });
      if (reqId === reqRef.current && res.data.success) {
        setReceipt(res.data.receipt);
        setIsOwner(res.data.is_owner !== false);
        if (res.data.shared_by_name) setSharedByName(res.data.shared_by_name);
        if (res.data.is_owner === false && Array.isArray(res.data.groups)) {
          setSharingInfo(res.data.groups);
        }
      }
    } catch (e) {
      if (reqId === reqRef.current) {
        if (e.response?.status === 401) navigate("/login", { replace: true });
        else setError("Unable to load receipt");
      }
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchReceipt(); }, [fetchReceipt]);

  // Fetch sharing info and available groups (owner) + reimbursement info (all users)
  useEffect(() => {
    if (!id) return;
    if (isOwner) {
      const fetchSharingInfo = async () => {
        try {
          const [sharingRes, groupsRes] = await Promise.all([
            axios.get(`${API}/my-shared-items?item_id=${id}`, { withCredentials: true }),
            axios.get(`${API}/groups?active_only=false`, { withCredentials: true }),
          ]);
          const items = sharingRes.data.shared_items || [];
          setSharingInfo(items);
          setAllGroups(groupsRes.data.groups || []);
          if (items.length > 0) {
            fetchReimbursementInfo(items);
          }
        } catch { /* silent */ }
      };
      fetchSharingInfo();
    }
  }, [id, isOwner]);

  // For non-owners: fetch reimbursement info using sharingInfo populated from receipt fetch
  useEffect(() => {
    if (!id || isOwner || sharingInfo.length === 0) return;
    fetchReimbursementInfo(sharingInfo);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isOwner, sharingInfo.length]);

  const fetchReimbursementInfo = async (items) => {
    // Fast path: my-shared-items already returns reimbursement fields (updated server)
    const reimb = items.find(s => s.reimbursement_status || s.paid_by_type);
    if (reimb) {
      setShareReimbInfo({
        share_id:             reimb.share_id,
        group_id:             reimb.group_id,
        group_name:           reimb.group_name,
        paid_by_type:         reimb.paid_by_type,
        paid_by_name:         reimb.paid_by_name,
        paid_by_label_name:   reimb.paid_by_label_name,
        reimbursement_status: reimb.reimbursement_status,
        reimbursed_at:        reimb.reimbursed_at,
        reimbursement_note:   reimb.reimbursement_note,
        // Owner fetched via my-shared-items so they are always the sharer
        is_uploader:          isOwner,
      });
      return;
    }

    // Fallback: fetch directly from the group's item list (works regardless of server version)
    for (const item of items) {
      if (!item.group_id) continue;
      try {
        const res = await axios.get(
          `${API}/groups/${item.group_id}/items?type=receipt`,
          { withCredentials: true }
        );
        const found = (res.data.items || []).find(i => i.item?.id === id || i.item_id === id);
        if (found && (found.paid_by_type || found.reimbursement_status)) {
          setShareReimbInfo({
            share_id:             found.share_id,
            group_id:             item.group_id,
            group_name:           item.group_name,
            paid_by_type:         found.paid_by_type,
            paid_by_name:         found.paid_by_name,
            paid_by_label_name:   found.paid_by_label_name,
            reimbursement_status: found.reimbursement_status,
            reimbursed_at:        found.reimbursed_at,
            reimbursement_note:   found.reimbursement_note,
            // is_uploader: person who shared to the circle (shared_by) matches current user
            is_uploader:          found.shared_by === authUser?.id || isOwner,
          });
          return;
        }
      } catch { /* silent */ }
    }
  };

  // When a circle is selected from the picker, check if it has reimbursement tracking
  const handleCircleSelect = async (group) => {
    setShowSharePicker(false);
    try {
      // Check if reimbursement tracking is enabled for this circle
      const groupRes = await axios.get(`${API}/groups/${group.id}`, { withCredentials: true });
      const reimb = groupRes.data.group?.reimbursement_tracking_enabled || false;

      if (!reimb) {
        await handleShareToGroup(group, null);
        return;
      }

      // Reimbursement is on — fetch members and labels independently
      const myName = `${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim();
      let members = [];
      let labels = [];

      try {
        const summaryRes = await axios.get(`${API}/groups/${group.id}/summary`, { withCredentials: true });
        members = (summaryRes.data.members || []).map(m => ({
          user_id: m.user_id || m.email,
          name: m.display_name || m.email,
          avatar_url: m.avatar_url,
        }));
      } catch { /* show sheet with empty member list */ }

      try {
        const labelsRes = await axios.get(`${API}/groups/${group.id}/labels`, { withCredentials: true });
        labels = labelsRes.data.labels || [];
      } catch { /* show sheet with no labels */ }

      // Set defaults and show "Who paid?" sheet
      setCircleMembers(members);
      setCircleLabels(labels);
      setPaidByType("member");
      setPaidByUserId(authUser?.id || null);
      setPaidByName(myName);
      setPaidByLabelId(null);
      setPaidByLabelName(null);
      // Reimbursed by: group default → first label → none
      const grpData = groupRes.data.group || {};
      if (grpData.default_reimb_label_name) {
        const lbl = labels.find(l => l.name === grpData.default_reimb_label_name) || labels[0];
        setGettingReimbursed(true);
        setReimbLabelId(lbl?.id || null);
        setReimbLabelName(lbl?.name || grpData.default_reimb_label_name);
      } else if (grpData.default_reimb_user_id) {
        setGettingReimbursed(true);
        setReimbLabelId(grpData.default_reimb_user_id);
        setReimbLabelName(grpData.default_reimb_name || '');
      } else if (labels.length > 0) {
        setGettingReimbursed(true);
        setReimbLabelId(labels[0].id);
        setReimbLabelName(labels[0].name);
      } else {
        setGettingReimbursed(false);
        setReimbLabelId(null);
        setReimbLabelName(null);
      }
      setShowPaidByPicker(false);
      setShowReimbPicker(false);
      setPaidBySearch('');
      setReimbSearch('');
      setPendingShareGroup(group);
    } catch {
      // If group fetch itself fails, share directly
      await handleShareToGroup(group, null);
    }
  };

  const handleConfirmShare = async () => {
    if (!pendingShareGroup) return;
    let reimb_status = "not_applicable";
    let finalPaidByLabelId = null;
    let finalPaidByLabelName = null;
    if (paidByType === "label") {
      reimb_status = "not_applicable"; // label paid directly
      finalPaidByLabelId = paidByLabelId;
      finalPaidByLabelName = paidByLabelName;
    } else if (paidByType === "member" && gettingReimbursed) {
      reimb_status = "pending";
      finalPaidByLabelId = reimbLabelId;
      finalPaidByLabelName = reimbLabelName;
    }
    await handleShareToGroup(pendingShareGroup, {
      paid_by_type: paidByType,
      paid_by_user_id: paidByType === "member" ? paidByUserId : null,
      paid_by_name: paidByType === "member" ? paidByName : null,
      paid_by_label_id: paidByType === "label" ? paidByLabelId : finalPaidByLabelId,
      paid_by_label_name: paidByType === "label" ? paidByLabelName : finalPaidByLabelName,
      reimbursement_status: reimb_status,
    });
    setPendingShareGroup(null);
  };

  const handleAddLabel = async () => {
    if (!newLabelName.trim() || !pendingShareGroup) return;
    setSavingLabel(true);
    try {
      const res = await axios.post(`${API}/groups/${pendingShareGroup.id}/labels`, {
        name: newLabelName.trim(),
        label_type: newLabelType,
      }, { withCredentials: true });
      const label = res.data.label;
      setCircleLabels(prev => [...prev, label]);
      setNewLabelName("");
      setShowAddLabel(false);
      toast.success("Label added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't add label");
    } finally { setSavingLabel(false); }
  };

  const handleMarkReceived = async () => {
    if (!shareReimbInfo) return;
    setMarkingReceived(true);
    try {
      const res = await axios.post(
        `${API}/groups/${shareReimbInfo.group_id}/share/${shareReimbInfo.share_id}/mark-received`,
        { note: markReceivedNote || null },
        { withCredentials: true }
      );
      setShareReimbInfo(prev => ({ ...prev, reimbursement_status: "received", reimbursed_at: res.data.reimbursed_at, reimbursement_note: markReceivedNote || null }));
      setSharingInfo(prev => prev.map(s => s.share_id === shareReimbInfo.share_id ? { ...s, reimbursement_status: "received" } : s));
      setShowMarkReceived(false);
      setMarkReceivedNote("");
      toast.success("Marked as received");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update");
    } finally { setMarkingReceived(false); }
  };

  const handleUndoReceived = async () => {
    if (!shareReimbInfo) return;
    setUndoingReceived(true);
    try {
      await axios.post(
        `${API}/groups/${shareReimbInfo.group_id}/share/${shareReimbInfo.share_id}/undo-received`,
        {},
        { withCredentials: true }
      );
      setShareReimbInfo(prev => ({ ...prev, reimbursement_status: "pending", reimbursed_at: null, reimbursement_note: null }));
      setSharingInfo(prev => prev.map(s => s.share_id === shareReimbInfo.share_id ? { ...s, reimbursement_status: "pending" } : s));
      toast.success("Undo successful");
    } catch { toast.error("Couldn't undo"); }
    finally { setUndoingReceived(false); }
  };

  const handleShareToGroup = async (group, reimbPayload) => {
    setSharing(true);
    try {
      const body = { item_id: id, item_type: "receipt", ...(reimbPayload || {}) };
      const res = await axios.post(`${API}/groups/${group.id}/share`, body, { withCredentials: true });
      toast.success(`Shared with ${group.name}`);
      setSharingInfo(prev => [...prev, {
        share_id: res.data.shared_item?.id,
        group_id: group.id,
        group_name: group.name,
        group_type: group.type,
        member_count: group.member_count,
        reimbursement_status: reimbPayload?.reimbursement_status,
      }]);
      if (reimbPayload?.reimbursement_status === "pending") {
        setShareReimbInfo({
          share_id: res.data.shared_item?.id,
          group_id: group.id,
          group_name: group.name,
          paid_by_type: reimbPayload.paid_by_type,
          paid_by_name: reimbPayload.paid_by_name,
          paid_by_label_name: reimbPayload.paid_by_label_name,
          reimbursement_status: "pending",
          is_uploader: true,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't share");
    } finally { setSharing(false); }
  };

  const handleUnshare = async (shareId, groupName) => {
    const groupId = sharingInfo.find(s => s.share_id === shareId)?.group_id;
    if (!groupId) return;
    try {
      await axios.delete(`${API}/groups/${groupId}/share/${shareId}`, { withCredentials: true });
      toast.success(`Removed from ${groupName}`);
      setSharingInfo(prev => prev.filter(s => s.share_id !== shareId));
    } catch { toast.error("Couldn't unshare"); }
  };

  // Groups not yet shared with
  const unsharableGroups = allGroups.filter(g => !sharingInfo.some(s => s.group_id === g.id));

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/receipts/${id}`, { withCredentials: true });
      toast.success("Receipt deleted");
      navigate("/receipts", { replace: true });
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setIsDeleting(false);
      setShowDelete(false);
    }
  };

  // Guard: block delete if receipt is still shared in circles
  const handleDeleteClick = () => {
    if (sharingInfo.length > 0) {
      const count = sharingInfo.length;
      toast.error(
        `Unshare from ${count === 1 ? "your circle" : `all ${count} circles`} before deleting`
      );
      return;
    }
    setShowDelete(true);
  };

  const handleNavClick = (key) => {
    if (key === "home") navigate("/dashboard");
    else if (key === "receipts") navigate("/receipts");
    else if (key === "cards") navigate("/cards");
    else if (key === "insights") { navigate("/insights"); return; }
    else if (key === "profile") { navigate("/profile"); return; }
    //else toast.info(`${key.charAt(0).toUpperCase() + key.slice(1)} coming soon`);
  };

  // ── Read directly from Claude-structured receipt document ─────────────────
  const currency        = receipt?.currency || "GBP";
  const total           = receipt?.amount ?? null;
  const imageUrl        = receipt?.receipt_image_url || null;
  const isPdfUrl        = imageUrl?.toLowerCase().endsWith('.pdf');
  const hasImage        = !!imageUrl && (isPdfUrl || !imgFailed);
  const merchantName    = receipt?.merchant || "Receipt";
  const category        = receipt?.category || "Other";
  const notes           = receipt?.notes || null;

  // Items (Claude schema: name, quantity, unit_price, original_total, discount_amount, discount_reason, final_total)
  const allItems        = Array.isArray(receipt?.items) ? receipt.items : [];

  // Totals breakdown
  const subtotal        = receipt?.subtotal ?? null;
  const totalDiscount   = receipt?.total_discount ?? null;
  const discounts       = Array.isArray(receipt?.discounts) ? receipt.discounts : [];

  // Payment
  const paymentMethod   = receipt?.payment_method || null;
  const cardType        = receipt?.card_type || null;
  const cardLast4       = receipt?.card_last4 || null;
  const paymentType     = receipt?.payment_type || null;

  // Loyalty (from Claude extraction, separate from loyalty card link)
  const loyaltyPointsEarned  = receipt?.loyalty_points_earned  ?? null;
  const loyaltyPointsBalance = receipt?.loyalty_points_balance ?? null;

  // VAT
  const vat             = receipt?.vat || null;

  // Receipt meta
  const receiptNumber   = receipt?.receipt_number || null;
  const customerName    = receipt?.customer_name  || null;

  // Date + time display
  const dateDisplay = (() => {
    const d = receipt?.transaction_date || receipt?.created_at;
    if (!d) return null;
    const base = formatReceiptDate(d);
    return base;
  })();
  const timeDisplay = receipt?.time || null;
  const location    = receipt?.location || null;

  const PREVIEW_COUNT = 5;
  const visibleItems = showAllItems ? allItems : allItems.slice(0, PREVIEW_COUNT);
  const hasMore = allItems.length > PREVIEW_COUNT;

  // ── Shared sections ───────────────────────────────────────────────────────

  const ImageSection = () => (
    <div
      className="relative cursor-pointer overflow-hidden"
      style={{ borderRadius: 16, backgroundColor: "#0F172A", minHeight: 180 }}
      onClick={() => {
        if (isPdfUrl) {
          window.open(imageUrl, '_blank');
        } else if (hasImage) {
          setShowLightbox(true);
        }
      }}
    >
      {isPdfUrl ? (
        <>
          <div
            className="w-full flex flex-col items-center justify-center gap-3"
            style={{ height: 180 }}
          >
            <div style={{ width: 56, height: 56, borderRadius: 14, backgroundColor: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FileText className="w-7 h-7" style={{ color: "#94A3B8" }} />
            </div>
            <p style={{ color: "#94A3B8", fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
              PDF Receipt
            </p>
          </div>
          <div
            className="absolute inset-0 flex items-end justify-center pb-3"
            style={{ background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)" }}
          >
            <span style={{ backgroundColor: "rgba(0,0,0,0.55)", color: "#FFFFFF", fontSize: 11, fontWeight: 600, padding: "5px 12px", borderRadius: 999, fontFamily: "DM Sans, sans-serif" }}>
              Tap to view PDF
            </span>
          </div>
        </>
      ) : hasImage ? (
        <>
          <img
            src={imageUrl}
            alt="Receipt"
            className="w-full"
            style={{ maxHeight: 260, objectFit: "cover", width: "100%", opacity: 0.92, imageOrientation: "from-image" }}
            onError={() => setImgFailed(true)}
          />
          <div
            className="absolute inset-0 flex items-end justify-center pb-3"
            style={{ background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)" }}
          >
            <span style={{ backgroundColor: "rgba(0,0,0,0.55)", color: "#FFFFFF", fontSize: 11, fontWeight: 600, padding: "5px 12px", borderRadius: 999, fontFamily: "DM Sans, sans-serif" }}>
              Tap to view full image
            </span>
          </div>
        </>
      ) : (
        <ImagePlaceholder height={180} />
      )}
    </div>
  );

  // Merchant + category + date
  const MerchantSection = () => (
    <SectionCard>
      <div className="flex items-center gap-3 p-4">
        <MerchantAvatar name={merchantName} size={48} />
        <div>
          <p style={{ fontSize: 18, fontWeight: 800, color: "#111827", fontFamily: "Manrope, sans-serif" }}>
            {merchantName}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <CategoryPill label={category} />
            {dateDisplay && (
              <span style={{ fontSize: 12, color: "#9CA3AF", fontFamily: "DM Sans, sans-serif" }}>
                · {dateDisplay}
              </span>
            )}
          </div>
          {sharingInfo.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {sharingInfo.map((s) => {
                const cfg = getGroupTypeConfig(s.group_type);
                const Icon = cfg.icon;
                return (
                  <span
                    key={s.group_id}
                    className="inline-flex items-center gap-1"
                    style={{
                      backgroundColor: cfg.bg,
                      color: '#FFFFFF',
                      fontSize: 11, fontWeight: 600,
                      fontFamily: 'DM Sans, sans-serif',
                      padding: '3px 8px', borderRadius: 999,
                    }}
                  >
                    {s.emoji
                      ? <span style={{ fontSize: 11, lineHeight: 1 }}>{s.emoji}</span>
                      : <Icon className="w-3 h-3" style={{ color: '#FFFFFF' }} />
                    }
                    {s.group_name}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );

  // Info rows (payment details — shown below items per new layout)
  const InfoSection = () => (
    <SectionCard>
      <div className="px-4">
        {(cardType || paymentMethod) && (
          <InfoRow
            icon={CreditCard}
            label="Payment"
            value={cardType && cardLast4 ? `${cardType} ···· ${cardLast4}` : paymentMethod}
          />
        )}
        {paymentType && (
          <InfoRow icon={CreditCard} label="Method" value={paymentType} />
        )}
        <InfoRow icon={Clock} label="Date" value={dateDisplay} />
        {timeDisplay && (
          <InfoRow icon={Clock} label="Time" value={timeDisplay} />
        )}
        {location && (
          <InfoRow icon={MapPin} label="Location" value={location} />
        )}
        {receiptNumber && (
          <InfoRow icon={Hash} label="Receipt No." value={`#${receiptNumber}`} isLink />
        )}
        {customerName && (
          <InfoRow icon={Hash} label="Customer" value={customerName} />
        )}
        {vat?.standard_rate > 0 && (
          <InfoRow icon={Hash} label="VAT (20%)" value={formatMoney(vat.standard_rate, currency)} />
        )}
      </div>
    </SectionCard>
  );

  // Total + items
  const TotalSection = () => (
    <SectionCard>
      <div className="p-4">
        {/* Total row */}
        <div className="flex items-end justify-between mb-4">
          <div>
            <p style={{ fontSize: 11, color: "#9CA3AF", fontFamily: "DM Sans, sans-serif", textTransform: "uppercase", letterSpacing: 1 }}>
              Total Paid
            </p>
            <p style={{ fontSize: 32, fontWeight: 900, color: "#111827", fontFamily: "Manrope, sans-serif", lineHeight: 1.1 }}>
              {total !== null ? formatMoney(total, currency) : "—"}
            </p>
          </div>
          {allItems.length > 0 && (
            <span style={{ backgroundColor: "#DCFCE7", color: "#15803D", fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 999, fontFamily: "DM Sans, sans-serif" }}>
              {allItems.length} items
            </span>
          )}
        </div>

        {/* Items list */}
        {allItems.length > 0 && (
          <>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#111827", fontFamily: "Manrope, sans-serif", marginBottom: 4 }}>
              Items
            </p>
            <div>
              {visibleItems.map((item, idx) => (
                <ItemRow key={item.id || idx} item={item} currency={currency} />
              ))}
            </div>

            {hasMore && (
              <button
                onClick={() => setShowAllItems(!showAllItems)}
                className="w-full flex items-center justify-center gap-2 mt-3 py-2.5 rounded-xl transition-colors"
                style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0", color: PRIMARY, fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}
              >
                {showAllItems
                  ? <><ChevronUp className="w-4 h-4" /> Show less</>
                  : <><ShoppingBag className="w-4 h-4" /> View all {allItems.length} items</>}
              </button>
            )}

            {/* Subtotal → discounts → total breakdown */}
            <div className="mt-4 pt-4" style={{ borderTop: "1px solid #F3F4F6" }}>
              {subtotal !== null && (
                <div className="flex justify-between py-1.5">
                  <span style={{ fontSize: 13, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Subtotal</span>
                  <span style={{ fontSize: 13, color: "#111827", fontFamily: "DM Sans, sans-serif" }}>{formatMoney(subtotal, currency)}</span>
                </div>
              )}
              {/* Per-discount lines */}
              {discounts.map((d, i) => (
                <div key={i} className="flex justify-between py-1.5">
                  <span style={{ fontSize: 13, color: "#16A34A", fontFamily: "DM Sans, sans-serif" }}>{d.description}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#16A34A", fontFamily: "DM Sans, sans-serif" }}>
                    -{formatMoney(d.amount, currency)}
                  </span>
                </div>
              ))}
              {/* Total discount summary if no per-line discounts */}
              {totalDiscount > 0 && discounts.length === 0 && (
                <div className="flex justify-between py-1.5">
                  <span style={{ fontSize: 13, color: "#16A34A", fontFamily: "DM Sans, sans-serif" }}>Total saved</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#16A34A", fontFamily: "DM Sans, sans-serif" }}>
                    -{formatMoney(totalDiscount, currency)}
                  </span>
                </div>
              )}
              {total !== null && (
                <div className="flex justify-between pt-2 mt-1" style={{ borderTop: "1px solid #F3F4F6" }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#111827", fontFamily: "Manrope, sans-serif" }}>Total</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: "#111827", fontFamily: "Manrope, sans-serif" }}>
                    {formatMoney(total, currency)}
                  </span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );

  // Loyalty — points earned from this receipt (Claude extraction)
  const LoyaltySection = () => {
    if (loyaltyPointsEarned === null && loyaltyPointsBalance === null) return null;
    return (
      <SectionCard>
        <div className="p-4">
          <p style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", fontFamily: "Manrope, sans-serif", marginBottom: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Loyalty points
          </p>
          <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: "#FFFBEB", border: "1px solid #FDE68A" }}>
            <div>
              {loyaltyPointsEarned !== null && (
                <p style={{ fontSize: 13, fontWeight: 700, color: "#92400E", fontFamily: "Manrope, sans-serif" }}>
                  +{loyaltyPointsEarned} pts earned
                </p>
              )}
              {loyaltyPointsBalance !== null && (
                <p style={{ fontSize: 12, color: "#B45309", fontFamily: "DM Sans, sans-serif", marginTop: 2 }}>
                  Balance: {loyaltyPointsBalance} pts
                </p>
              )}
            </div>
            <span style={{ fontSize: 20 }}>⭐</span>
          </div>
        </div>
      </SectionCard>
    );
  };

  // Notes
  const NotesSection = () => {
    if (!notes) return null;
    return (
      <SectionCard>
        <div className="p-4">
          <p style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", fontFamily: "Manrope, sans-serif", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Notes
          </p>
          <p style={{ fontSize: 14, color: "#374151", fontFamily: "DM Sans, sans-serif", lineHeight: 1.6 }}>
            {notes}
          </p>
        </div>
      </SectionCard>
    );
  };

  // Savings banner — shown when there are discounts
  const SavingsBanner = () => {
    const saved = totalDiscount > 0
      ? totalDiscount
      : discounts.reduce((sum, d) => sum + (d.amount || 0), 0);
    if (!saved || saved <= 0) return null;
    return (
      <div
        className="flex items-center gap-2 px-4 py-3 rounded-2xl"
        style={{ backgroundColor: "#F0FDF4", border: "1px solid #DCFCE7" }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>★</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#15803D", fontFamily: "DM Sans, sans-serif" }}>
          You saved {formatMoney(saved, currency)} on this shop
        </span>
      </div>
    );
  };

  // ── Paid By Section (reimbursement tracking) ─────────────────────────────
  const PaidBySection = () => {
    if (!shareReimbInfo) return null;
    const isPending = shareReimbInfo.reimbursement_status === "pending";
    const isReceived = shareReimbInfo.reimbursement_status === "received";
    const paidName = shareReimbInfo.paid_by_type === "label"
      ? (shareReimbInfo.paid_by_label_name || "Label")
      : (shareReimbInfo.paid_by_name || "Unknown");
    return (
      <div style={{ backgroundColor: "#FFFFFF", borderRadius: 18, padding: 16, boxShadow: "0 2px 12px rgba(15,23,42,0.06)", border: "1px solid #F3F4F6" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 style={{ color: "#111827", fontSize: 14, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
            Paid by
          </h3>
        </div>
        <div
          style={{
            backgroundColor: isPending ? "#FFFBEB" : isReceived ? "#F0FDFA" : "#F9FAFB",
            border: `1px solid ${isPending ? "#FCD34D" : isReceived ? "#99F6E4" : "#E5E7EB"}`,
            borderRadius: 12, padding: "10px 12px",
          }}
        >
          {/* Who paid */}
          <div className="flex items-center gap-2 mb-2">
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: shareReimbInfo.paid_by_type === "label" ? "#0D9488" : PRIMARY, color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}
            >
              {shareReimbInfo.paid_by_type === "label" ? <Banknote className="w-3.5 h-3.5" /> : paidName.charAt(0)}
            </div>
            <div className="flex-1">
              <p style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope, sans-serif" }}>{paidName}</p>
              {isPending && (
                <p style={{ fontSize: 11, color: "#B45309", fontFamily: "DM Sans, sans-serif", display: "flex", alignItems: "center", gap: 3 }}>
                  <Clock className="w-3 h-3" style={{ flexShrink: 0 }} />
                  Pending{shareReimbInfo.paid_by_label_name ? ` from ${shareReimbInfo.paid_by_label_name}` : ""}
                </p>
              )}
              {isReceived && (
                <p style={{ fontSize: 11, color: "#0D9488", fontFamily: "DM Sans, sans-serif" }}>
                  ✓ Received{shareReimbInfo.reimbursed_at ? ` · ${new Date(shareReimbInfo.reimbursed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}
                </p>
              )}
            </div>
            {isPending && (
              <span style={{ backgroundColor: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 5, padding: "1px 7px", fontSize: 10, fontWeight: 700, color: "#92400E", fontFamily: "DM Sans, sans-serif" }}>
                Pending
              </span>
            )}
            {isReceived && (
              <span style={{ backgroundColor: "#CCFBF1", border: "1px solid #99F6E4", borderRadius: 5, padding: "1px 7px", fontSize: 10, fontWeight: 700, color: "#065F46", fontFamily: "DM Sans, sans-serif" }}>
                Received
              </span>
            )}
          </div>

          {/* Note if received */}
          {isReceived && shareReimbInfo.reimbursement_note && (
            <p style={{ fontSize: 11, color: "#6B7280", fontFamily: "DM Sans, sans-serif", marginBottom: 8, fontStyle: "italic" }}>
              "{shareReimbInfo.reimbursement_note}"
            </p>
          )}

          {/* Mark as received — uploader only */}
          {isPending && shareReimbInfo.is_uploader && (
            <>
              {showMarkReceived ? (
                <div style={{ marginTop: 8 }}>
                  <input
                    type="text"
                    placeholder="Add a note e.g. received via bank transfer… (optional)"
                    value={markReceivedNote}
                    onChange={e => setMarkReceivedNote(e.target.value)}
                    style={{ width: "100%", border: "1px solid #FCD34D", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontFamily: "DM Sans, sans-serif", color: "#111827", outline: "none", marginBottom: 6 }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleMarkReceived}
                      disabled={markingReceived}
                      className="flex-1 flex items-center justify-center gap-1"
                      style={{ backgroundColor: "#D97706", borderRadius: 8, padding: "8px", color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "DM Sans, sans-serif", border: "none", cursor: "pointer", opacity: markingReceived ? 0.7 : 1 }}
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      {markingReceived ? "Saving…" : "Confirm"}
                    </button>
                    <button
                      onClick={() => { setShowMarkReceived(false); setMarkReceivedNote(""); }}
                      style={{ flex: 1, backgroundColor: "#F3F4F6", borderRadius: 8, padding: "8px", color: "#374151", fontSize: 12, fontWeight: 600, fontFamily: "DM Sans, sans-serif", border: "none", cursor: "pointer" }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowMarkReceived(true)}
                  className="w-full flex items-center justify-center gap-1.5"
                  style={{ backgroundColor: "#D97706", borderRadius: 9, padding: "9px", color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: "DM Sans, sans-serif", border: "none", cursor: "pointer", marginTop: 6 }}
                >
                  <CheckCircle className="w-4 h-4" />
                  Mark as received
                </button>
              )}
            </>
          )}

          {/* Undo received */}
          {isReceived && shareReimbInfo.is_uploader && (
            <button
              onClick={handleUndoReceived}
              disabled={undoingReceived}
              className="flex items-center gap-1 mt-2"
              style={{ color: "#6B7280", fontSize: 11, fontWeight: 600, fontFamily: "DM Sans, sans-serif", background: "none", border: "none", cursor: "pointer", padding: 0 }}
            >
              <RotateCcw className="w-3 h-3" />
              {undoingReceived ? "Undoing…" : "Undo"}
            </button>
          )}
        </div>
      </div>
    );
  };

  // ── Who Paid? bottom sheet (reimbursement share flow) ─────────────────────
  const WhoPaidSheet = () => {
    if (!pendingShareGroup) return null;
    const myName = `${authUser?.first_name || ""} ${authUser?.last_name || ""}`.trim();
    // Display name depends on who paid — member name or label name
    const paidByIsLabel = paidByType === "label";
    const displayName = paidByIsLabel ? (paidByLabelName || 'Label') : (paidByName || myName);
    const reimbDisplay = !gettingReimbursed ? 'Not getting reimbursed' : (reimbLabelName || 'Select...');
    const isReimbLabel = gettingReimbursed && reimbLabelId && !String(reimbLabelId).startsWith('user_');

    return (
      <div className="fixed inset-0" style={{ zIndex: 100 }}>
        <div onClick={() => { setPendingShareGroup(null); setShowPaidByPicker(false); setShowReimbPicker(false); }} className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,0.4)" }} />
        <div className="absolute left-0 right-0 bottom-0 flex flex-col" style={{ backgroundColor: "#FFFFFF", borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: "90vh", overflowY: "auto", padding: "0 20px 48px", boxShadow: "0 -8px 32px rgba(0,0,0,0.15)" }}>
          <div className="flex justify-center pt-3 mb-3"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: "#D1D5DB" }} /></div>

          {/* Header */}
          <div style={{ background: "linear-gradient(135deg, #2D5BFF 0%, #1B3DBF 100%)", borderRadius: 14, padding: "14px 16px", marginBottom: 24 }}>
            <p style={{ color: "#fff", fontSize: 17, fontWeight: 700, fontFamily: "Manrope, sans-serif", marginBottom: 2 }}>Share to {pendingShareGroup?.name}</p>
            <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>
              {receipt?.merchant} · {receipt?.amount != null ? formatCurrency(receipt.amount, receipt.currency) : ""}
            </p>
          </div>

          {/* ── Paid by row ───────────────────────────────────────────── */}
          <div style={{ marginBottom: 14 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#6B7280", marginBottom: 8, fontFamily: "DM Sans, sans-serif" }}>Paid by</p>
            <button onClick={() => { setShowPaidByPicker(v => !v); setShowReimbPicker(false); setPaidBySearch(""); }}
              className="w-full flex items-center gap-3"
              style={{ backgroundColor: "#F8FAFF", border: `1.5px solid ${showPaidByPicker ? "#2D5BFF" : "#E5E7EB"}`, borderRadius: showPaidByPicker ? "12px 12px 0 0" : 12, padding: "12px 14px", cursor: "pointer", textAlign: "left" }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: paidByIsLabel ? "#0D9488" : "#2D5BFF", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                {paidByIsLabel ? <Banknote className="w-4 h-4" style={{ color: "#fff" }} /> : displayName.charAt(0).toUpperCase()}
              </div>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: "#111827", fontFamily: "Manrope, sans-serif" }}>
                {displayName}
                {!paidByIsLabel && (paidByUserId === authUser?.id || !paidByUserId) && <span style={{ color: "#9CA3AF", fontSize: 12, marginLeft: 4 }}>(You)</span>}
              </span>
              <ChevronRight className="w-4 h-4" style={{ color: "#9CA3AF", transform: showPaidByPicker ? "rotate(90deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }} />
            </button>

            {showPaidByPicker && (
              <div style={{ border: "1.5px solid #2D5BFF", borderTop: "none", borderRadius: "0 0 12px 12px", overflow: "hidden" }}>
                <div style={{ padding: "8px 12px", borderBottom: "1px solid #F3F4F6", backgroundColor: "#F8FAFF" }}>
                  <input type="text" placeholder="Search members..." value={paidBySearch} onChange={e => setPaidBySearch(e.target.value)}
                    style={{ width: "100%", fontSize: 16, fontFamily: "DM Sans, sans-serif", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 10px", outline: "none" }} />
                </div>
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  {/* Members section */}
                  {[{ user_id: authUser?.id, name: myName }, ...circleMembers.filter(m => {
                    if (authUser?.id && m.user_id === authUser.id) return false;
                    if (authUser?.email && (m.user_id === authUser.email || m.email === authUser.email)) return false;
                    return true;
                  })].filter(m => !paidBySearch || m.name.toLowerCase().includes(paidBySearch.toLowerCase()))
                    .map(m => {
                      const sel = paidByType === "member" && (paidByUserId === m.user_id || (!paidByUserId && m.user_id === authUser?.id));
                      return (
                        <button key={m.user_id} onClick={() => { setPaidByType("member"); setPaidByUserId(m.user_id); setPaidByName(m.name); setPaidByLabelId(null); setPaidByLabelName(null); setShowPaidByPicker(false); setPaidBySearch(""); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5"
                          style={{ backgroundColor: sel ? "#EFF6FF" : "transparent", borderBottom: "1px solid #F9FAFB", cursor: "pointer", textAlign: "left" }}>
                          <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: sel ? "#2D5BFF" : "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{m.name.charAt(0)}</div>
                          <span style={{ flex: 1, fontSize: 14, color: "#111827", fontFamily: "DM Sans, sans-serif" }}>
                            {m.name}{m.user_id === authUser?.id && <span style={{ color: "#9CA3AF", fontSize: 12, marginLeft: 4 }}>(You)</span>}
                          </span>
                          {sel && <CheckCircle className="w-4 h-4" style={{ color: "#2D5BFF", flexShrink: 0 }} />}
                        </button>
                      );
                    })}
                  {/* Labels section — direct payment (no reimbursement needed) */}
                  {circleLabels.filter(l => !paidBySearch || l.name.toLowerCase().includes(paidBySearch.toLowerCase())).length > 0 && (
                    <>
                      <div style={{ padding: "5px 12px", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", backgroundColor: "#F9FAFB", fontFamily: "DM Sans, sans-serif" }}>Direct payment (no reimbursement)</div>
                      {circleLabels.filter(l => !paidBySearch || l.name.toLowerCase().includes(paidBySearch.toLowerCase())).map(lbl => {
                        const sel = paidByType === "label" && paidByLabelId === lbl.id;
                        return (
                          <button key={lbl.id} onClick={() => { setPaidByType("label"); setPaidByLabelId(lbl.id); setPaidByLabelName(lbl.name); setPaidByUserId(null); setPaidByName(null); setGettingReimbursed(false); setReimbLabelId(null); setReimbLabelName(null); setShowPaidByPicker(false); setPaidBySearch(""); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5"
                            style={{ backgroundColor: sel ? "#F0FDFA" : "transparent", borderBottom: "1px solid #F9FAFB", cursor: "pointer", textAlign: "left" }}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: sel ? "#0D9488" : "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Banknote className="w-3.5 h-3.5" style={{ color: sel ? "#fff" : "#0D9488" }} /></div>
                            <span style={{ flex: 1, fontSize: 14, color: "#111827", fontFamily: "DM Sans, sans-serif" }}>{lbl.name}</span>
                            {sel && <CheckCircle className="w-4 h-4" style={{ color: "#0D9488", flexShrink: 0 }} />}
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Reimbursed by — always shown, defaults to None ───────── */}
          <div style={{ marginBottom: 28 }}>
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#6B7280", marginBottom: 8, fontFamily: "DM Sans, sans-serif" }}>Reimbursed by</p>
            <button onClick={() => { setShowReimbPicker(v => !v); setShowPaidByPicker(false); setReimbSearch(""); }}
              className="w-full flex items-center gap-3"
              style={{ backgroundColor: !gettingReimbursed ? "#F9FAFB" : "#F0FDFA", border: `1.5px solid ${showReimbPicker ? "#0D9488" : !gettingReimbursed ? "#E5E7EB" : "#99F6E4"}`, borderRadius: showReimbPicker ? "12px 12px 0 0" : 12, padding: "12px 14px", cursor: "pointer", textAlign: "left" }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, backgroundColor: !gettingReimbursed ? "#F3F4F6" : isReimbLabel ? "#0D9488" : "#2D5BFF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {!gettingReimbursed ? <span style={{ color: "#9CA3AF", fontSize: 18 }}>–</span>
                 : isReimbLabel ? <Banknote className="w-4 h-4" style={{ color: "#fff" }} />
                 : <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>{(reimbLabelName || "?").charAt(0)}</span>}
              </div>
              <span style={{ flex: 1, fontSize: 15, fontWeight: 600, color: !gettingReimbursed ? "#9CA3AF" : "#111827", fontFamily: "Manrope, sans-serif" }}>{reimbDisplay}</span>
              <ChevronRight className="w-4 h-4" style={{ color: "#9CA3AF", transform: showReimbPicker ? "rotate(90deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }} />
            </button>

            {showReimbPicker && (
              <div style={{ border: "1.5px solid #0D9488", borderTop: "none", borderRadius: "0 0 12px 12px", overflow: "hidden" }}>
                <div style={{ padding: "8px 12px", borderBottom: "1px solid #F3F4F6", backgroundColor: "#F8FFFE" }}>
                  <input type="text" placeholder="Search..." value={reimbSearch} onChange={e => setReimbSearch(e.target.value)}
                    style={{ width: "100%", fontSize: 16, fontFamily: "DM Sans, sans-serif", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 10px", outline: "none" }} />
                </div>
                <div style={{ maxHeight: 260, overflowY: "auto" }}>
                  {!reimbSearch && (
                    <button onClick={() => { setGettingReimbursed(false); setReimbLabelId(null); setReimbLabelName(null); setShowReimbPicker(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5"
                      style={{ backgroundColor: !gettingReimbursed ? "#F9FAFB" : "transparent", borderBottom: "1px solid #F3F4F6", cursor: "pointer" }}>
                      <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><span style={{ color: "#9CA3AF", fontSize: 16 }}>–</span></div>
                      <span style={{ flex: 1, fontSize: 14, color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>Not getting reimbursed</span>
                      {!gettingReimbursed && <CheckCircle className="w-4 h-4" style={{ color: "#6B7280", flexShrink: 0 }} />}
                    </button>
                  )}
                  {(() => {
                    // Exclude the payer from reimb options
                    const filtered = circleMembers.filter(m => {
                      if (m.user_id === paidByUserId) return false; // can't reimburse yourself
                      return !reimbSearch || m.name.toLowerCase().includes(reimbSearch.toLowerCase());
                    });
                    if (!filtered.length) return null;
                    return (<>
                      <div style={{ padding: "5px 12px", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", backgroundColor: "#F9FAFB", fontFamily: "DM Sans, sans-serif" }}>Members</div>
                      {filtered.map(m => {
                        const sel = gettingReimbursed && reimbLabelId === m.user_id;
                        return (
                          <button key={m.user_id} onClick={() => { setGettingReimbursed(true); setReimbLabelId(m.user_id); setReimbLabelName(m.name); setShowReimbPicker(false); setReimbSearch(""); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5"
                            style={{ backgroundColor: sel ? "#EFF6FF" : "transparent", borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: sel ? "#2D5BFF" : "#6B7280", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{m.name.charAt(0)}</div>
                            <span style={{ flex: 1, fontSize: 14, color: "#111827", fontFamily: "DM Sans, sans-serif" }}>{m.name}</span>
                            {sel && <CheckCircle className="w-4 h-4" style={{ color: "#2D5BFF", flexShrink: 0 }} />}
                          </button>
                        );
                      })}
                    </>);
                  })()}
                  {(() => {
                    // Exclude the paid-by label from reimb options
                    const filtered = circleLabels.filter(l => {
                      if (paidByIsLabel && l.id === paidByLabelId) return false;
                      return !reimbSearch || l.name.toLowerCase().includes(reimbSearch.toLowerCase());
                    });
                    if (!filtered.length) return null;
                    return (<>
                      <div style={{ padding: "5px 12px", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.5px", backgroundColor: "#F9FAFB", fontFamily: "DM Sans, sans-serif" }}>Payment labels</div>
                      {filtered.map(lbl => {
                        const sel = gettingReimbursed && reimbLabelId === lbl.id;
                        return (
                          <button key={lbl.id} onClick={() => { setGettingReimbursed(true); setReimbLabelId(lbl.id); setReimbLabelName(lbl.name); setShowReimbPicker(false); setReimbSearch(""); }}
                            className="w-full flex items-center gap-3 px-3 py-2.5"
                            style={{ backgroundColor: sel ? "#F0FDFA" : "transparent", borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, backgroundColor: sel ? "#0D9488" : "#D1FAE5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Banknote className="w-3.5 h-3.5" style={{ color: sel ? "#fff" : "#0D9488" }} /></div>
                            <span style={{ flex: 1, fontSize: 14, color: "#111827", fontFamily: "DM Sans, sans-serif" }}>{lbl.name}</span>
                            {sel && <CheckCircle className="w-4 h-4" style={{ color: "#0D9488", flexShrink: 0 }} />}
                          </button>
                        );
                      })}
                    </>);
                  })()}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleConfirmShare}
            disabled={sharing}
            className="w-full flex items-center justify-center gap-2"
            style={{ background: sharing ? "#93C5FD" : "linear-gradient(135deg, #2D5BFF, #1B3DBF)", borderRadius: 16, padding: 14, color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "DM Sans, sans-serif", border: "none", cursor: "pointer" }}
          >
            {sharing ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            {sharing ? "Sharing…" : "Confirm & share →"}
          </button>
        </div>
      </div>
    );
  };

  // Circle sharing section
  const GroupSharingSection = () => (
    <div style={{ backgroundColor: "#FFFFFF", borderRadius: 18, padding: 16, boxShadow: "0 2px 12px rgba(15,23,42,0.06)", border: "1px solid #F3F4F6" }}>
      <div className="flex items-center justify-between mb-3">
        <h3 style={{ color: "#111827", fontSize: 14, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>Circle sharing</h3>
      </div>

      {/* Personal — always shown */}
      <div className="flex items-center gap-3 py-2.5" style={{ borderBottom: "1px solid #F3F4F6" }}>
        <div className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: PRIMARY, color: "#FFFFFF", fontWeight: 700, fontSize: 12, fontFamily: "Manrope, sans-serif" }}>
          {(authUser?.first_name || 'U').charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <p style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>Personal</p>
          <p style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>Only you can see this</p>
        </div>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 8, backgroundColor: "#F3F4F6", color: "#6B7280", fontSize: 11, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
          <Lock className="w-3 h-3" /> Private
        </span>
      </div>

      {/* Shared circles */}
      {sharingInfo.map((s) => {
        const cfg = getGroupTypeConfig(s.group_type);
        const Icon = cfg.icon;
        const circleStatus = allGroups.find(g => g.id === s.group_id)?.status;
        const isClosed = circleStatus === "closed";
        return (
          <div key={s.share_id} className="flex items-center gap-3 py-2.5" style={{ borderBottom: "1px solid #F3F4F6" }}>
            <div className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: cfg.bg }}>
              {s.emoji
                ? <span style={{ fontSize: 16, lineHeight: 1 }}>{s.emoji}</span>
                : <Icon className="w-4 h-4" style={{ color: "#FFFFFF" }} />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{s.group_name}</p>
              <p style={{ color: isClosed ? "#D97706" : "#16A34A", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>
                {isClosed ? "Circle closed · history preserved" : `Shared · ${s.member_count} member${s.member_count !== 1 ? "s" : ""} can see this`}
              </p>
            </div>
            {isClosed ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 8, backgroundColor: "#FFFBEB", color: "#D97706", fontSize: 11, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>
                <Lock className="w-3 h-3" /> Closed
              </span>
            ) : (
              <button onClick={() => handleUnshare(s.share_id, s.group_name)} className="flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: "#FEE2E2" }}>
                <X className="w-3.5 h-3.5" style={{ color: "#EF4444" }} />
              </button>
            )}
          </div>
        );
      })}

      {/* Share with another circle */}
      {unsharableGroups.length > 0 && (
        <button onClick={() => setShowSharePicker(true)} className="flex items-center gap-3 py-2.5 w-full">
          <div className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 999, backgroundColor: "#EEF2FF" }}>
            <Users className="w-4 h-4" style={{ color: PRIMARY }} />
          </div>
          <p style={{ color: PRIMARY, fontSize: 14, fontWeight: 600, fontFamily: "DM Sans, sans-serif" }}>Share with a circle</p>
        </button>
      )}

      {/* Group picker bottom sheet */}
      {showSharePicker && (
        <>
          <div onClick={() => setShowSharePicker(false)} className="fixed inset-0" style={{ backgroundColor: "rgba(0,0,0,0.4)", zIndex: 200 }} />
          <div className="fixed left-0 right-0 bottom-0" style={{ backgroundColor: "#FFFFFF", borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: "12px 20px 80px", boxShadow: "0 -8px 32px rgba(0,0,0,0.15)", zIndex: 201, maxHeight: "70vh", overflowY: "auto" }}>
            <div className="flex justify-center mb-4"><div style={{ width: 36, height: 4, borderRadius: 999, backgroundColor: "#D1D5DB" }} /></div>
            <p style={{ color: "#6B7280", fontSize: 12, fontWeight: 700, fontFamily: "DM Sans, sans-serif", letterSpacing: 1, marginBottom: 16 }}>SHARE WITH A CIRCLE</p>
            {unsharableGroups.map((g) => {
              const cfg = getGroupTypeConfig(g.type);
              const Icon = cfg.icon;
              return (
                <button key={g.id} onClick={() => handleCircleSelect(g)} disabled={sharing} className="w-full flex items-center gap-3 py-3" style={{ borderBottom: "1px solid #F3F4F6", opacity: sharing ? 0.6 : 1 }}>
                  <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, backgroundColor: cfg.bg }}>
                    {g.emoji
                      ? <span style={{ fontSize: 20, lineHeight: 1 }}>{g.emoji}</span>
                      : <Icon className="w-5 h-5" style={{ color: "#FFFFFF" }} />
                    }
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p style={{ color: "#111827", fontSize: 15, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>{g.name}</p>
                    <p style={{ color: "#9CA3AF", fontSize: 13, fontFamily: "DM Sans, sans-serif", marginTop: 1 }}>{g.member_count} member{g.member_count !== 1 ? "s" : ""}</p>
                  </div>
                </button>
              );
            })}
            {sharing && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" style={{ color: PRIMARY }} /></div>}
          </div>
        </>
      )}
    </div>
  );

  // Delete button
  const DeleteButton = () => (
    <button
      onClick={handleDeleteClick}
      className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl transition-colors"
      style={{
        backgroundColor: "#FFF1F2",
        border: "1px solid #FECDD3",
        color: "#EF4444",
        fontSize: 14,
        fontWeight: 600,
        fontFamily: "DM Sans, sans-serif",
      }}
    >
      <Trash2 className="w-4 h-4" />
      Delete receipt
    </button>
  );

  // ── MOBILE ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#F5F7FA" }}>

        {/* Mobile Header */}
        <div className="flex items-center justify-between px-4 py-4" style={{ background: GRADIENT }}>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1"
            style={{ color: "#FFFFFF", fontSize: 14, fontWeight: 500, fontFamily: "DM Sans, sans-serif" }}
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
          <span style={{ color: "#FFFFFF", fontSize: 16, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
            Receipt
          </span>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center"
            style={{ width: 32, height: 32, borderRadius: 999, backgroundColor: 'rgba(255,255,255,0.18)' }}
            aria-label="Close"
          >
            <X className="w-5 h-5" style={{ color: '#FFFFFF' }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 px-4 pt-4 space-y-3 overflow-y-auto" style={{ paddingBottom: "calc(80px + env(safe-area-inset-bottom, 34px))" }}>
          {loading ? (
            <SkeletonPage />
          ) : error ? (
            <div className="p-6 text-center">
              <p style={{ color: "#6B7280", fontFamily: "DM Sans, sans-serif" }}>{error}</p>
              <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 rounded-full text-white text-sm font-semibold" style={{ background: GRADIENT }}>
                Go Back
              </button>
            </div>
          ) : receipt ? (
            <>
              <ImageSection />
              <MerchantSection />
              <SavingsBanner />
              <TotalSection />
              <InfoSection />
              <LoyaltySection />
              <NotesSection />
              {isOwner && <GroupSharingSection />}
              {shareReimbInfo && <PaidBySection />}
              {isOwner && <DeleteButton />}
              {!isOwner && (
                <div className="flex items-start gap-2.5" style={{ padding: "12px 16px", borderRadius: 14, backgroundColor: "#EEF2FF", border: "1px solid #C7D2FE" }}>
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#4338CA" }} />
                  <div>
                    <p style={{ color: "#3730A3", fontSize: 13, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>
                      Shared by <strong>{sharedByName || "a circle member"}</strong> · You can view but not edit
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Bottom Nav */}
        <nav
          className="fixed bottom-0 left-0 right-0 flex justify-around items-center z-50"
          style={{
            backgroundColor: "#FFFFFF",
            borderTop: "1px solid #F3F4F6",
            paddingTop: 10,
            paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          {NAV_ITEMS.map((item) => {
            const active = item.key === "receipts";
            return (
              <button
                key={item.key}
                onClick={() => handleNavClick(item.key)}
                className="flex flex-col items-center gap-1"
              >
                <item.icon
                  className="w-5 h-5"
                  style={{ color: active ? PRIMARY : "#9CA3AF" }}
                  fill={active ? PRIMARY : "none"}
                />
                <span style={{
                  color: active ? PRIMARY : "#9CA3AF",
                  fontSize: 11,
                  fontFamily: "DM Sans, sans-serif",
                  fontWeight: active ? 600 : 500,
                }}>
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        {showDelete && (
          <DeleteDialog onCancel={() => setShowDelete(false)} onConfirm={handleDelete} isDeleting={isDeleting} />
        )}
        {showLightbox && hasImage && (
          <Lightbox url={imageUrl} onClose={() => setShowLightbox(false)} />
        )}
        <WhoPaidSheet />
      </div>
    );
  }

  // ── DESKTOP ───────────────────────────────────────────────────────────────
  const userName = authUser?.first_name || authUser?.name || "User";
  const lastName = authUser?.last_name || "";
  const avatarUrl = authUser?.avatar_url;
  const firstInitial = userName.charAt(0).toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: "#F5F7FA" }}>

      {/* Sidebar */}
      <aside
        className="flex flex-col flex-shrink-0"
        style={{ width: 220, backgroundColor: "#FFFFFF", borderRight: "1px solid #F3F4F6" }}
      >
        <div className="flex items-center gap-2 px-5 py-5" style={{ borderBottom: "1px solid #F3F4F6" }}>
          <img src="/mybuddy-animated.png" alt="My Buddy" width={28} height={28} />
          <span style={{ color: "#111827", fontSize: 18, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>
            My Buddy
          </span>
        </div>
        <nav className="flex-1 px-3 py-3">
          {NAV_ITEMS.map((item) => {
            const active = item.key === "receipts";
            return (
              <button
                key={item.key}
                onClick={() => handleNavClick(item.key)}
                className="w-full flex items-center gap-3 px-4 py-3 mb-1 transition-all"
                style={{
                  backgroundColor: active ? "#E0E7FF" : "transparent",
                  color: active ? PRIMARY : "#6B7280",
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: active ? 600 : 500,
                  fontFamily: "DM Sans, sans-serif",
                }}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: "#F3F4F6" }}>
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center overflow-hidden flex-shrink-0 rounded-full"
              style={{
                width: 40, height: 40,
                backgroundColor: PRIMARY, color: "#FFFFFF",
                fontWeight: 700, fontSize: 14,
                fontFamily: "Manrope, sans-serif",
              }}
            >
              {avatarUrl
                ? <img src={avatarUrl} alt={userName} className="w-full h-full object-cover" />
                : `${firstInitial}${lastName?.charAt(0)?.toUpperCase() || ""}`
              }
            </div>
            <div className="min-w-0">
              <p className="truncate" style={{ color: "#111827", fontSize: 14, fontWeight: 600, fontFamily: "Manrope, sans-serif" }}>
                {userName} {lastName}
              </p>
              <p className="truncate" style={{ color: "#6B7280", fontSize: 12, fontFamily: "DM Sans, sans-serif" }}>
                {authUser?.email || ""}
              </p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col overflow-hidden">

        {/* Desktop Header */}
        <div
          className="flex items-center justify-between px-8"
          style={{ background: GRADIENT, minHeight: 72, flexShrink: 0 }}
        >
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1"
            style={{ color: "#FFFFFF", fontSize: 14, fontWeight: 500, fontFamily: "DM Sans, sans-serif" }}
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
          <span style={{ color: "#FFFFFF", fontSize: 18, fontWeight: 700, fontFamily: "Manrope, sans-serif" }}>
            Receipt Detail
          </span>
          <div style={{ width: 28 }} />
        </div>

        {/* Two-column content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <SkeletonPage />
          ) : error ? (
            <div className="p-12 text-center">
              <p style={{ color: "#6B7280" }}>{error}</p>
              <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 rounded-full text-white text-sm font-semibold" style={{ background: GRADIENT }}>
                Go Back
              </button>
            </div>
          ) : receipt ? (
            <div className="flex gap-5 max-w-5xl mx-auto">
              <div className="flex-1 min-w-0 space-y-4">
                <ImageSection />
                <TotalSection />
                <InfoSection />
              </div>
              <div className="flex-shrink-0 space-y-4" style={{ width: 300 }}>
                <MerchantSection />
                <SavingsBanner />
                <LoyaltySection />
                <NotesSection />
                {isOwner && <GroupSharingSection />}
                {shareReimbInfo && <PaidBySection />}
                {isOwner && <DeleteButton />}
                {!isOwner && (
                  <div className="flex items-start gap-2.5" style={{ padding: "12px 16px", borderRadius: 14, backgroundColor: "#EEF2FF", border: "1px solid #C7D2FE" }}>
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "#4338CA" }} />
                    <div>
                      <p style={{ color: "#3730A3", fontSize: 13, fontFamily: "DM Sans, sans-serif", lineHeight: 1.5 }}>
                        Shared by <strong>{sharedByName || "a circle member"}</strong> · You can view but not edit
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </main>

      {showDelete && (
        <DeleteDialog onCancel={() => setShowDelete(false)} onConfirm={handleDelete} isDeleting={isDeleting} />
      )}
      {showLightbox && hasImage && (
        <Lightbox url={imageUrl} onClose={() => setShowLightbox(false)} />
      )}
      <WhoPaidSheet />
    </div>
  );
}